import type { FC } from "react";

import { Controller, type UseFormReturn } from "react-hook-form";

import { Icon, Input, Select, Text, Tooltip } from "@alpacahq/ui/old";

import { Field } from "~/v2/components/form";
import { getPercentTransformer } from "../../common/util";
import type { TransferSchema } from "../../transferForm/schema";
import type { IraDistributionReason } from "./distributionReasons";

type IraDistributionFieldsProps = {
  form: UseFormReturn<TransferSchema>;
  eligibleReasons: IraDistributionReason[];
  areInputsDisabled?: boolean;
};

export const IraDistributionFields: FC<IraDistributionFieldsProps> = ({
  form,
  eligibleReasons,
  areInputsDisabled,
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = form;

  const selectedReason = watch("ira.distribution_reason");
  const helperText = eligibleReasons.find(
    (reason) => reason.distributionReason === selectedReason,
  )?.helperText;

  const fedPct = Number(watch("ira.fed_pct"));
  const statePct = Number(watch("ira.state_pct"));
  const isPctInRange = (value: number) => value > 0 && value <= 100;
  const isCombinedWithholdingInvalid =
    isPctInRange(fedPct) && isPctInRange(statePct) && fedPct + statePct > 100;

  return (
    <div className="grid grid-cols-1 gap-4 py-4 min-[500px]:grid-cols-2">
      <Field
        label="Withdrawal Reason"
        className="min-[500px]:col-span-2 [&_label]:flex-1"
        tooltip={
          helperText ? (
            <Tooltip content={helperText} align="end">
              <Icon name="InformationCircle" />
            </Tooltip>
          ) : undefined
        }
      >
        <Controller
          name="ira.distribution_reason"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              disabled={areInputsDisabled}
              errorMsg={errors.ira?.distribution_reason?.message}
              placeholder="Select a withdrawal reason"
              options={eligibleReasons.map((reason) => ({
                label: reason.label,
                value: reason.distributionReason,
              }))}
            />
          )}
        />
      </Field>
      <Field label="Federal Tax Withholding">
        <Controller
          name="ira.fed_pct"
          control={control}
          render={({ field }) => (
            <Input
              {...getPercentTransformer({ field })}
              disabled={areInputsDisabled}
              errorMsg={errors.ira?.fed_pct?.message}
              placeholder="0"
              rightIcon={<Text>%</Text>}
            />
          )}
        />
      </Field>
      <Field label="State Tax Withholding">
        <Controller
          name="ira.state_pct"
          control={control}
          render={({ field }) => (
            <Input
              {...getPercentTransformer({ field })}
              disabled={areInputsDisabled}
              errorMsg={errors.ira?.state_pct?.message}
              placeholder="0"
              rightIcon={<Text>%</Text>}
            />
          )}
        />
      </Field>
      {isCombinedWithholdingInvalid && (
        <Text className="text-red-500 text-sm min-[500px]:col-span-2">
          Combined federal and state withholding cannot exceed 100%.
        </Text>
      )}
    </div>
  );
};
